import React, { useState, useMemo, useRef, useEffect } from "react";
import { X, Search, ChevronDown, Check, Loader2, HelpCircle } from "lucide-react";
import axios from "axios";
import { Tooltip } from "@mui/material";
import heartLoading from "../../../assests/heartLoading.gif";

const Checkbox = ({ checked, indeterminate, onChange }) => (
  <div
    onClick={onChange}
    className={`
      w-[16px] h-[16px] rounded-[4px] flex items-center justify-center cursor-pointer
      flex-shrink-0 transition-all duration-150 border-[2px]
      ${checked || indeterminate ? "bg-[#6C5CE7] border-[#6C5CE7]" : "bg-white border-[#B4B2A9]"}
    `}
  >
    {indeterminate && !checked && <div className="w-[8px] h-[2px] bg-white rounded-[1px]" />}
    {checked && <Check size={10} color="#fff" strokeWidth={3} />}
  </div>
);

const RuleKeySelect = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`
          flex items-center justify-between gap-[6px] w-[140px] px-[10px] py-[5px]
          text-[13px] rounded-[8px] border bg-white cursor-pointer outline-none
          ${value ? "border-[#7F77DD] text-[#3C3489]" : "border-[#D3D1C7] text-[#888780]"}
        `}
      >
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
          {value || "Select key..."}
        </span>
        <ChevronDown size={13} className="flex-shrink-0 text-[#888780]" />
      </button>

      {open && (
        <div
          className="absolute top-[calc(100%+4px)] left-0 z-[101] bg-white
            border border-[#D3D1C7] rounded-[10px] min-w-[140px] overflow-hidden
            shadow-[0_4px_16px_rgba(0,0,0,0.10)]"
        >
          {options.map((key) => (
            <div
              key={key}
              onClick={() => { onChange(key); setOpen(false); }}
              className={`
                flex items-center justify-between px-[14px] py-[9px]
                text-[13px] text-[#3C3489] cursor-pointer transition-colors duration-100
                ${value === key ? "bg-[#EEEDFE]" : "hover:bg-[#F5F4FE]"}
              `}
            >
              <span>{key}</span>
              {value === key && <Check size={13} color="#534AB7" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const isValidJson = (str) => {
  if (!str.trim()) return true;
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
};

const DQModal = ({ open, onClose, productId, productName, onSuccess, onFailed }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState({});
  const [additionalParams, setAdditionalParams] = useState({});
  const [jsonErrors, setJsonErrors] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !productId) return;
    setLoading(true);
    setSelected({});
    setAdditionalParams({});
    setJsonErrors({});
    setSearch("");
    setAssets([]);

    axios
      .get(`${process.env.REACT_APP_API_URL}/atRest/RuleKeys/${productId}`)
      .then((res) => setAssets(res.data?.assets || []))
      .catch((err) => console.error("Failed to fetch DQ assets:", err))
      .finally(() => setLoading(false));
  }, [open, productId]);

  const filtered = useMemo(
    () => assets.filter((a) => a.data_asset_name.toLowerCase().includes(search.toLowerCase())),
    [assets, search]
  );

  const selectedIds = Object.keys(selected);
  const totalSelected = selectedIds.length;
  const allChecked = assets.length > 0 && totalSelected === assets.length;
  const indeterminate = totalSelected > 0 && totalSelected < assets.length;
  const hasJsonErrors = Object.values(jsonErrors).some(Boolean);
  const canRun = totalSelected > 0 && !hasJsonErrors;

  const toggleSelectAll = () => {
    if (allChecked) {
      setSelected({});
    } else {
      const all = {};
      assets.forEach((a) => { all[a.data_asset_id] = selected[a.data_asset_id] || ""; });
      setSelected(all);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[id] !== undefined) delete next[id];
      else next[id] = "";
      return next;
    });
  };

  const handleRuleKey = (id, value) => {
    setSelected((prev) => ({ ...prev, [id]: value }));
  };

  const handleAdditionalParams = (id, value) => {
    setAdditionalParams((prev) => ({ ...prev, [id]: value }));
    setJsonErrors((prev) => ({ ...prev, [id]: value.trim() !== "" && !isValidJson(value) }));
  };

  const handleRunDQ = async () => {
    const payload = selectedIds.map((id) => {
      const paramStr = (additionalParams[id] || "").trim();
      return {
        assetId: id,
        ruleKey: selected[id] || "",
        additionalParams: paramStr ? JSON.parse(paramStr) : null,
      };
    });

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/atRest/trigger`, payload);
      onSuccess?.(data?.message);
      onClose();
    } catch (err) {
      console.error("DQ trigger failed:", err);
      onFailed?.(err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-[16px]"
      style={{ background: "rgba(60,50,120,0.18)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-[780px] bg-white rounded-[16px] overflow-hidden flex flex-col max-h-[85vh]
          shadow-[0_8px_40px_rgba(80,60,180,0.13)]"
      >
        <div className="px-[22px] pt-[18px] pb-[14px] border-b border-[#EEEDFE]">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="m-0 text-[16px] font-semibold text-[#26215C]">Run DQ at Rest</h2>
              <p className="mt-[3px] mb-0 text-[12.5px] text-[#888780]">
                Select assets for{" "}
                <span className="text-[#6C5CE7] font-medium">{productName}</span>
              </p>
            </div>
            <div className="flex items-center gap-[8px]">
          <Tooltip
            title="To check the DQ Job status and results, please proceed with the versioning."
            placement="left"
            arrow
          >
            <span className="flex items-center cursor-pointer text-[#888780] hover:text-[#6C5CE7] transition-colors duration-150">
              <HelpCircle size={17} />
            </span>
          </Tooltip>

          <button
            onClick={onClose}
            className="bg-transparent border-none cursor-pointer text-[#888780]
              p-[2px] flex items-center hover:opacity-70 transition-opacity"
          >
            <X size={18} />
          </button>
        </div>
          </div>

          {!loading && (
            <div className="flex items-center gap-[12px] mt-[14px]">
              <label className="flex items-center gap-[8px] cursor-pointer flex-shrink-0">
                <Checkbox
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onChange={toggleSelectAll}
                />
                <span className="text-[13px] font-medium text-[#3C3489]">Select all</span>
                <span className="text-[12.5px] text-[#888780]">
                  {totalSelected > 0
                    ? `${totalSelected} of ${assets.length} selected`
                    : `${assets.length} assets`}
                </span>
              </label>

              <div
                className="flex-1 flex items-center gap-[8px] border border-[#D3D1C7]
                  rounded-[8px] px-[10px] py-[6px] bg-[#FAFAFA]"
              >
                <Search size={13} className="text-[#B4B2A9] flex-shrink-0" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search assets..."
                  className="border-none outline-none bg-transparent text-[13px]
                    text-[#26215C] flex-1 min-w-0 placeholder:text-[#B4B2A9]"
                />
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center py-[48px] gap-[12px]">
            <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
            <p className="text-[13px] text-[#888780] m-0">Loading data...</p>
          </div>
        ) : (
          <>
            <div
              className="grid items-center px-[22px] py-[7px] bg-[#F8F7FE] border-b border-[#EEEDFE]"
              style={{ gridTemplateColumns: "32px 1fr 150px 1fr" }}
            >
              <div />
              <span className="text-[11px] font-semibold text-[#888780] uppercase tracking-[0.06em]">
                Asset Name
              </span>
              <span className="text-[11px] font-semibold text-[#888780] uppercase tracking-[0.06em]">
                Rule Key
              </span>
              <span className="text-[11px] font-semibold text-[#888780] uppercase tracking-[0.06em]">
                Additional Parameters
              </span>
            </div>

            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <div className="px-[22px] py-[32px] text-center text-[13px] text-[#B4B2A9]">
                  {assets.length === 0
                    ? "No assets found for this product."
                    : "No assets match your search."}
                </div>
              ) : (
                filtered.map((asset, idx) => {
                  const isChecked = selected[asset.data_asset_id] !== undefined;
                  const ruleVal = selected[asset.data_asset_id] || "";
                  const paramVal = additionalParams[asset.data_asset_id] || "";
                  const paramError = jsonErrors[asset.data_asset_id];

                  return (
                    <div
                      key={asset.data_asset_id}
                      className={`
                        grid items-start px-[22px] py-[10px] border-b border-[#F1EFE8]
                        transition-colors duration-100
                        ${isChecked ? "bg-[#FAFAFE]" : idx % 2 === 0 ? "bg-white" : "bg-[#FDFCFF]"}
                      `}
                      style={{ gridTemplateColumns: "32px 1fr 150px 1fr" }}
                    >
                      <div className="pt-[2px]">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => toggleRow(asset.data_asset_id)}
                        />
                      </div>

                      <span
                        className={`
                          text-[13px] font-mono overflow-hidden text-ellipsis
                          whitespace-nowrap pr-[12px] pt-[4px]
                          ${isChecked ? "text-[#26215C]" : "text-[#5F5E5A]"}
                        `}
                      >
                        <Tooltip title={asset.data_asset_name} placement="top">
                        {asset.data_asset_name}
                        </Tooltip>
                      </span>

                      {isChecked ? (
                        <RuleKeySelect
                          value={ruleVal}
                          onChange={(val) => handleRuleKey(asset.data_asset_id, val)}
                          options={asset.rule_selection_keys || []}
                        />
                      ) : (
                        <span className="text-[18px] text-[#D3D1C7] select-none">—</span>
                      )}

                      {isChecked ? (
                        <div className="pl-[12px] flex flex-col gap-[4px]">
                          <input
                            value={paramVal}
                            onChange={(e) => handleAdditionalParams(asset.data_asset_id, e.target.value)}
                            placeholder='{"key": "value"}'
                            className={`
                              w-full px-[10px] py-[5px] text-[12px] rounded-[8px] border
                              outline-none bg-white font-mono placeholder:text-[#C4C2BA]
                              ${paramError
                                ? "border-[#E24B4A] text-[#E24B4A]"
                                : "border-[#D3D1C7] text-[#26215C] focus:border-[#7F77DD]"
                              }
                            `}
                          />
                          {paramError && (
                            <span className="text-[11px] text-[#E24B4A]">
                              Must be a valid JSON object
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[18px] text-[#D3D1C7] select-none pl-[12px]">—</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        <div
          className="px-[22px] py-[12px] border-t border-[#EEEDFE]
            flex items-center justify-end gap-[10px] bg-white"
        >
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-[20px] py-[8px] rounded-[8px] border border-[#D3D1C7]
              bg-white text-[#5F5E5A] text-[13px] font-medium cursor-pointer
              hover:bg-[#F5F4F4] transition-colors duration-150 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            disabled={!canRun || submitting}
            onClick={handleRunDQ}
            className={`
              flex items-center gap-[6px] px-[20px] py-[8px] rounded-[8px] border-none
              text-white text-[13px] font-semibold transition-colors duration-200
              ${canRun && !submitting
                ? "bg-[#6C5CE7] cursor-pointer hover:bg-[#534AB7]"
                : "bg-[#AFA9EC] cursor-not-allowed"
              }
            `}
          >
            {submitting && <Loader2 size={13} className="animate-spin" />}
            {totalSelected > 0 ? `Run DQ (${totalSelected})` : "Run DQ"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DQModal;